import { createSlice } from '@reduxjs/toolkit';
import { getFieldEngagement,
    getFieldExtendedRegion,
    getFieldEngagementSegment } from '../thunks/engagementThunks';
import { createDistrictUSAMapData,
    getOverallPhysicians,
    handlePeriodChange,
    makeBarDataSeries,
    createStateUSAMapData } from '../../utility';

const fieldEngagementIntialState = {
    isLoading: true,
    isFieldTriggerEngagementLoading: false,
    isFieldRegionEngagementLoading: false,
    error: null,
    engagementTotal: 0,
    previousEngagement: 0,
    isPeriodChangeIncrease: false,
    periodChange: 0,
    overallPhysicians: 0,
    fieldTriggerEngagement: [],
    fieldTriggerEngagementChart: [],
    distributionRegion: [],
    distributionRegionSegment: [],
    districtBreakdownUSAMapData: [],
    stateBreakdownUSAMapData: [],
    stateBreakdownUSAMapSegmentData: [],
    providers: [],
    filteredProviders: [],
    currentPage: 1,
    pageSize: 10,
    totalRecordsFetch: 0,
    totalPages: 0,
    fieldPhysicianSegments: [],
    allRegionalEngagement: [],
    allStateEngagement: [],
    regionExtendedEngagement: [],
    stateExtendedEngagement: [],
    stateMapSeries: [],
    stateDistributionRegion: [],
    dismissedEngagement: [],
    completedEngagement: [],
    overallTerritoriesPhysicians: 0,
    distributionTerritories: [],
    territoriesBreakdownUSAMapData: []
};

const fieldEngagement = createSlice({
    name: 'fieldEngagement',
    initialState: fieldEngagementIntialState,
    reducers: {
        resetToInitialState(state, action) {
            Object.assign(state, fieldEngagementIntialState);
        },
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        handleSearch: (state, action) => {
            const { searchTerm } = action.payload;

            // Transforming providers
            const updatedProviders = state.providers.map(provider => ({
                ...provider,
                name: provider.first_name + ' ' + provider.last_name,
                address: provider.address1 + ', ' + provider.city + ', ' + provider.state + ',' + provider.zip,
                segmentations: Array.isArray(provider.segmentation) ? provider.segmentation.join(', ') : provider.segmentation,
                channels_engageds: Array.isArray(provider.channels_engaged) ? provider.channels_engaged.join(', ') : provider.channels_engaged,
                vendor_engageds: Array.isArray(provider.vendor_engaged) ? provider.vendor_engaged.join(', ') : provider.vendor_engaged,
                channels_engaged_dates: Array.isArray(provider.channels_engaged_date) ? provider.channels_engaged_date.join(', ') : provider.channels_engaged_date
            }));

            // Filtering providers based on searchTerm
            const filteredProviders = updatedProviders.filter(provider => {
                const providerData = Object.values(provider)
                    .map(value => (typeof value === 'string' ? value.trim().toLowerCase() : value))
                    .join(' ');
                const trimmedSearchTerm = searchTerm?.trim()?.toLowerCase();
                return providerData.includes(trimmedSearchTerm);
            });

            return {
                ...state,
                filteredProviders,
                totalRecordsFetch: filteredProviders.length,
                totalPages: Math.ceil(filteredProviders.length / (state.pageSize ?? 1)),
                currentPage: 1
            };
        },
        resetStates(state,action){
            state.distributionRegion=[];
        },
        regionByStatusAndSegment(state, action) {
            let statusKey = '';

            if (action.payload.status === 'completed') {
                statusKey = 'marked_as_complete_vod_c';
            }

            if (action.payload.status === 'dismissed') {
                statusKey = 'dismissed_vod_c';
            }

            const regionExtendedEngagement = filterAndGroupData(state.allStateEngagement, action.payload.segment, statusKey, 'region');
            const stateExtendedEngagement = filterAndGroupData(state.allStateEngagement, action.payload.segment, statusKey, 'state');

            state.overallPhysicians = getOverallPhysicians(regionExtendedEngagement || [], true);
            state.distributionRegionSegment = makeBarDataSeries(regionExtendedEngagement, true) || [];
            state.stateBreakdownUSAMapSegmentData = stateExtendedEngagement;
        }

    },
    extraReducers: (builder) => {
        builder
            /* Get Field Engagement */
            .addCase(getFieldEngagement.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                state.engagementTotal = action.payload.engagement?.currentEngagement;
                state.previousEngagement = action.payload?.engagement?.previousEngagement;
                const providersData = action.payload.engagement?.providers || [];
                state.providers = providersData;
                state.filteredProviders = providersData;
                state.totalRecordsFetch = providersData?.length;
                state.totalPages = Math.ceil((providersData?.length ?? 0) / state.pageSize);
                state.fieldTriggerEngagementChart = getFieldPhycianEngagementData(action.payload?.engagement?.segmentEngagement || []);

                /* Period Change */
                const { period, isPeriodChangeIncrease } = handlePeriodChange(action.payload.engagement?.currentEngagement,action.payload.engagement?.previousEngagement);
                state.periodChange = period;
                state.isPeriodChangeIncrease = isPeriodChangeIncrease;

                /* Map functions */

                const finalStates = action.payload?.engagement?.stateEngagement.map(item => {
                    if (item.segment === 'Unassigned' || item.segment === 'UNASSIGNED') {
                        return {
                            ...item,
                            segment: 'Unassigned',
                            region: 'Unassigned'
                        };
                    }
                    return item;
                });
                state.allRegionalEngagement = finalStates || [];
                state.allStateEngagement = finalStates || [];
                //state.stateOverallPhysicians = getOverallPhysicians(action.payload.engagement?.stateEngagement || []);

                const regionExtendedEngagement = filterAndGroupData(finalStates || [], undefined, undefined, 'region');
                state.distributionRegionSegment = makeBarDataSeries(regionExtendedEngagement, true) || [];
                state.stateBreakdownUSAMapSegmentData = filterAndGroupData(finalStates || [], undefined, undefined, 'state');


                /* Breakdown Region */
                state.distributionRegion = makeBarDataSeries(action.payload.engagement?.regionalEngagement) || [];
                state.stateDistributionRegion = makeBarDataSeries(action.payload?.engagement?.stateEngagement,false,true) || [];
                state.districtBreakdownUSAMapData = createDistrictUSAMapData(action.payload.engagement?.regionalEngagement) || [];
                state.stateBreakdownUSAMapData = createDistrictUSAMapData(action.payload.engagement?.stateEngagement) || [];
                state.stateMapSeries = createStateUSAMapData(action.payload?.engagement?.stateEngagement || []);
                state.overallPhysicians = getOverallPhysicians(action.payload.engagement?.regionalEngagement || []);
                // territories map
                state.overallTerritoriesPhysicians = getOverallPhysicians(action.payload.engagement?.territoryEngagement || []);
                state.distributionTerritories = makeBarDataSeries(action.payload?.engagement?.territoryEngagement || []) || [];
                state.territoriesBreakdownUSAMapData = createDistrictUSAMapData(action.payload?.engagement?.territoryEngagement || [] ) ;
                let segmentOptions = providersData.map(physician => {
                    if (physician.segmentation && physician.segmentation.length > 0) {
                        return Array.isArray(physician.segmentation) ? physician.segmentation[0] : physician.segmentation;
                    }
                }).filter(segment => segment !== undefined);
                segmentOptions = segmentOptions.map(segment => {
                    if (typeof segment === 'string' && segment.toLowerCase() === 'unassigned') {
                        return 'Unassigned';
                    }
                    return segment;
                });

                segmentOptions = [...new Set(segmentOptions)];
                segmentOptions = segmentOptions.map(segment => (
                    { value: segment, label: segment }
                ));

                segmentOptions.unshift({ label: 'All', value: 'All' });
                state.fieldPhysicianSegments = segmentOptions;
            })
            .addCase(getFieldEngagement.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getFieldEngagement.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            /* Get Field Engagement Segment */
            .addCase(getFieldEngagementSegment.fulfilled, (state, action) => {
                state.isFieldTriggerEngagementLoading = false;
                state.error = null;
                let dismissedData = action.payload?.engagement?.dismissed_stats || {};
                dismissedData = convertDismissedData(dismissedData);
                state.dismissedEngagement = dismissedData || [];
                let completedData = action.payload?.engagement?.completed_stats || {};
                completedData = convertCompletedData(completedData);
                state.completedEngagement = completedData || [];
                state.fieldTriggerEngagement = getFieldEngagementData(
                    action.payload?.engagement?.fieldEngagement || []);
            })
            .addCase(getFieldEngagementSegment.pending, (state, action) => {
                state.isFieldTriggerEngagementLoading = true;
                state.error = null;
            })
            .addCase(getFieldEngagementSegment.rejected, (state, action) => {
                state.isFieldTriggerEngagementLoading = false;
                state.error = action.payload;
            })
            /* Get Field Extended Region */
            .addCase(getFieldExtendedRegion.fulfilled, (state, action) => {
                state.isFieldRegionEngagementLoading = false;
                state.error = null;
                const finalStates = action.payload?.engagement?.stateEngagement.map(item => {
                    if (item.segment === 'Unassigned' || item.segment === 'UNASSIGNED') {
                        return {
                            ...item,
                            segment: 'Unassigned',
                            region: 'Unassigned'
                        };
                    }
                    return item;
                });
                state.allRegionalEngagement = finalStates || [];
                state.allStateEngagement = finalStates || [];
                //state.stateOverallPhysicians = getOverallPhysicians(action.payload.engagement?.stateEngagement || []);

                const regionExtendedEngagement = filterAndGroupData(finalStates || [], undefined, undefined, 'region');
                state.distributionRegion = makeBarDataSeries(regionExtendedEngagement, true) || [];
                state.stateBreakdownUSAMapData = filterAndGroupData(finalStates || [], undefined, undefined, 'state');
            })
            .addCase(getFieldExtendedRegion.pending, (state, action) => {
                state.isFieldRegionEngagementLoading = true;
                state.error = null;
            })
            .addCase(getFieldExtendedRegion.rejected, (state, action) => {
                state.isFieldRegionEngagementLoading = false;
                state.error = action.payload;
            });
    }
});

const getFieldTriggerCalculatedData = (data) => {
    let OverallResponseRate = 0;
    let Actioned = 0;
    let AverageTimetoRespond = 0;
    let Dismissed = 0;
    let Completed = 0;

    if (data && data.Responsed !== 0) {
        OverallResponseRate = (((data.Actioned + data.Dismissed + data.Completed) / data.Responsed) * 100).toFixed(2);
        Completed = ((data.Completed / data.Responsed) * 100).toFixed(2);
        Actioned = ((data.Actioned / data.Responsed) * 100).toFixed(2);
        Dismissed = ((data.Dismissed / data.Responsed) * 100).toFixed(2);
        AverageTimetoRespond = Math.abs(data.AvgTimeToResponsed).toFixed(2);
    }

    // Calculate percent change for each field
    let PrevOverallResponseRate = 0;
    let PrevActioned = 0;
    let PrevCompleted = 0;
    let PrevDismissed = 0;
    let PrevAverageTimetoRespond = 0;

    if (data && data.PrevResponsed !== 0) {
        PrevOverallResponseRate = (((data.PrevActioned + data.PrevDismissed + data.PrevCompleted) / data.PrevResponsed) * 100).toFixed(2);
        PrevCompleted = ((data.PrevCompleted / data.PrevResponsed) * 100).toFixed(2);
        PrevActioned = ((data.PrevActioned / data.PrevResponsed) * 100).toFixed(2);
        PrevDismissed = ((data.PrevDismissed / data.PrevResponsed) * 100).toFixed(2);
        PrevAverageTimetoRespond = Math.abs(data.PrevAvgTimeToResponsed).toFixed(2);
    }

    return {
        OverallResponseRate: { current: OverallResponseRate, previous: PrevOverallResponseRate },
        Completed: { current: Completed, previous: PrevCompleted },
        Actioned: { current: Actioned, previous: PrevActioned },
        Dismissed: { current: Dismissed, previous: PrevDismissed },
        AverageTimetoRespond: { current: AverageTimetoRespond, previous: PrevAverageTimetoRespond }
    };
};

const getFieldEngagementData = (data) =>{
    const {
        OverallResponseRate,Actioned,AverageTimetoRespond,Dismissed,Completed
    } =getFieldTriggerCalculatedData(data);
    return [
        {
            name: 'Overall Response Rate',
            volume: `${OverallResponseRate.current}%`,
            current: `${OverallResponseRate.current}`,
            previousVolume: `${OverallResponseRate.previous}`
        },
        {
            name: 'Average Time to Respond (days)',
            volume: `${AverageTimetoRespond.current}`,
            current: `${AverageTimetoRespond.current}`,
            previousVolume: `${AverageTimetoRespond.previous}`
        },
        {
            name: 'Actioned',
            volume: `${Actioned.current}%`,
            current: `${Actioned.current}`,
            previousVolume: `${Actioned.previous}`
        },
        {
            name: 'Dismissed',
            volume: `${Dismissed.current}%`,
            current: `${Dismissed.current}`,
            previousVolume: `${Dismissed.previous}`
        },
        {
            name: 'Completed',
            volume: `${Completed.current}%`,
            current: `${Completed.current}`,
            previousVolume: `${Completed.previous}`
        }
    ];

};

const getFieldPhycianEngagementData = (data) => {
    let finalData = [];
    if(data && Object.keys(data).length > 0){
        finalData = Object.entries(data).map(([name, value]) => ({
            name: name,
            y: parseFloat(value)
        }));
    }

    return finalData;
};

const filterAndGroupData = (data, segment, statusKey, via) => {
    const filteredData = data.filter(item => {
        if ((segment && item.segment === segment) || !segment) {
            if ((statusKey && item[statusKey] > 0) || !statusKey) {
                return true;
            }
        }
        return false;
    });

    const groupedData = {};
    filteredData.forEach(item => {
        const state = item[via];
        const latitude = item.latitude ? parseFloat(item.latitude.toString()) : item.latitude;
        const longitude = item.longitude ? parseFloat(item.longitude.toString()) : item.longitude;

        if (!groupedData[state.toLowerCase()]) {
            groupedData[state.toLowerCase()] = {
                keyword: state,
                z: 0,
                lat: latitude,
                lon: longitude
            };
        }

        groupedData[state.toLowerCase()].z += item.count;
    });

    return Object.values(groupedData);
};

const convertDismissedData = (data = {}) => {
    if (typeof data !== 'object' || Array.isArray(data) || Object.keys(data).length === 0) {
        return [];
    }

    const feedbackMap = {
        dismiss_feedback1_vod_c: 'Reassign to different rep',
        dismiss_feedback2_vod_c: 'Physician no longer active',
        dismiss_feedback3_vod_c: 'Recent call with physician',
        dismiss_feedback4_vod_c: 'Other'
    };

    const totalCount = Object.values(data).reduce((sum, feedback) => {
        if (typeof feedback.count !== 'number' || feedback.count < 0) {
            return 0;
        }
        return sum + feedback.count;
    }, 0);

    if (totalCount === 0) {
        return [];
    }

    return Object.entries(data).map(([key, value]) => {
        if (!feedbackMap[key] || typeof value.count !== 'number' || value.count < 0) {
            return null;
        }

        return {
            name: feedbackMap[key],
            count: value.count,
            percent: ((value.count / totalCount) * 100).toFixed(2)
        };
    }).filter(Boolean);
};

const convertCompletedData = (data = {}) => {
    if (typeof data !== 'object' || Array.isArray(data) || Object.keys(data).length === 0) {
        return [];
    }

    const totalCount = Object.values(data).reduce((sum, item) => {
        if (typeof item.count !== 'number' || item.count < 0) {
            return 0;
        }
        return sum + item.count;
    }, 0);

    if (totalCount === 0) {
        return [];
    }

    return Object.entries(data).map(([key, value]) => {
        if (typeof key !== 'string' || typeof value.count !== 'number' || value.count < 0) {
            return null;
        }

        return {
            name: key,
            count: value.count,
            percent: ((value.count / totalCount) * 100).toFixed(2)
        };
    }).filter(Boolean);
};


export const { actions } = fieldEngagement;
export default fieldEngagement.reducer;
